/**
 * Ed25519 Relay Module — Stateless PRF-Derived Stellar Signer
 *
 * Derives an ed25519 keypair from WebAuthn PRF output via HKDF,
 * signs transaction payloads, and encodes the public key as a
 * Stellar G-address (StrKey). Nothing is stored — keys exist only
 * during the signing operation and are immediately discarded.
 *
 * Flow: Face ID → PRF output (32 bytes) → HKDF → ed25519 seed → keypair
 *
 * Dependencies:
 * - @noble/ed25519 for ed25519 operations
 * - Web Crypto for HKDF derivation
 */

import * as ed from '@noble/ed25519';

// Use Web Crypto SHA-512 for @noble/ed25519 v3 async operations
// (required in browser environments — v3 doesn't bundle a hash)
if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.subtle !== 'undefined') {
  ed.hashes.sha512Async = async (message: Uint8Array): Promise<Uint8Array> => {
    // Copy to a fresh ArrayBuffer to satisfy strict TS typing
    const buf = new ArrayBuffer(message.length);
    new Uint8Array(buf).set(message);
    const hash = await crypto.subtle.digest('SHA-512', buf);
    return new Uint8Array(hash);
  };
}

// ============================================================================
// HKDF Derivation (Web Crypto native)
// ============================================================================

/**
 * Derive a 32-byte ed25519 seed from PRF output using HKDF-SHA256.
 *
 * Even though the PRF output is already 32 bytes of high-entropy material,
 * HKDF adds domain separation — ensuring the same PRF output used for
 * different purposes (e.g., AES encryption vs ed25519 derivation) produces
 * different keys.
 */
export async function deriveEd25519Seed(prfOutput: Uint8Array): Promise<Uint8Array> {
  // Import PRF output as HKDF key material
  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    prfOutput,
    'HKDF',
    false,
    ['deriveBits']
  );

  // Derive 32 bytes (256 bits) for ed25519 seed
  const seedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32), // empty salt (all zeros)
      info: new TextEncoder().encode('lumenbro:ed25519:seed:v1'),
    },
    hkdfKey,
    256
  );

  return new Uint8Array(seedBits);
}

// ============================================================================
// Ed25519 Operations
// ============================================================================

/**
 * Derive ed25519 keypair from PRF output.
 * Returns public key (32 bytes) and private seed (32 bytes).
 */
export async function deriveEd25519Keypair(prfOutput: Uint8Array): Promise<{
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}> {
  const seed = await deriveEd25519Seed(prfOutput);
  const publicKey = await ed.getPublicKeyAsync(seed);
  return { publicKey, privateKey: seed };
}

/**
 * Sign a message with ed25519 private key (seed).
 * Returns 64-byte signature.
 */
export async function signEd25519(
  message: Uint8Array,
  privateKey: Uint8Array
): Promise<Uint8Array> {
  return ed.signAsync(message, privateKey);
}

// ============================================================================
// Stellar StrKey Encoding (G-address)
// ============================================================================

// Base32 alphabet (RFC 4648)
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * Encode bytes as base32 (RFC 4648, no padding).
 */
function base32Encode(data: Uint8Array): string {
  let result = '';
  let bits = 0;
  let value = 0;

  for (let i = 0; i < data.length; i++) {
    value = (value << 8) | data[i];
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      result += BASE32_ALPHABET[(value >>> bits) & 0x1f];
    }
  }

  // Handle remaining bits
  if (bits > 0) {
    result += BASE32_ALPHABET[(value << (5 - bits)) & 0x1f];
  }

  return result;
}

/**
 * CRC16-XModem checksum (used by Stellar StrKey).
 */
function crc16xmodem(data: Uint8Array): Uint8Array {
  let crc = 0x0000;

  for (let i = 0; i < data.length; i++) {
    let code = (crc >>> 8) & 0xff;
    code ^= data[i] & 0xff;
    code ^= code >>> 4;
    crc = (crc << 8) & 0xffff;
    crc ^= code;
    crc ^= (code << 5) & 0xffff;
    crc ^= (code << 12) & 0xffff;
  }

  // Return as little-endian 2 bytes
  return new Uint8Array([crc & 0xff, (crc >>> 8) & 0xff]);
}

/**
 * Encode a 32-byte ed25519 public key as a Stellar G-address.
 *
 * Format: base32(versionByte + publicKey + crc16xmodem(versionByte + publicKey))
 * Version byte for public key (G-address): 6 << 3 = 48
 * Result: 56-character string starting with 'G'
 */
export function publicKeyToStellarAddress(publicKey: Uint8Array): string {
  if (publicKey.length !== 32) {
    throw new Error(`Invalid public key length: ${publicKey.length}, expected 32`);
  }

  // Version byte for ed25519 public key = 6 << 3 = 48
  const versionByte = 6 << 3; // 48

  // Payload = version + public key (33 bytes)
  const payload = new Uint8Array(33);
  payload[0] = versionByte;
  payload.set(publicKey, 1);

  // Checksum = CRC16-XModem of payload (2 bytes)
  const checksum = crc16xmodem(payload);

  // Full data = payload + checksum (35 bytes)
  const full = new Uint8Array(35);
  full.set(payload, 0);
  full.set(checksum, 33);

  // Base32 encode → 56 characters starting with 'G'
  return base32Encode(full);
}

/**
 * Encode a 32-byte ed25519 seed as a Stellar S-address (secret key).
 *
 * Format: base32(versionByte + seed + crc16xmodem(versionByte + seed))
 * Version byte for secret key (S-address): 18 << 3 = 144
 * Result: 56-character string starting with 'S'
 */
export function seedToStellarSecret(seed: Uint8Array): string {
  if (seed.length !== 32) {
    throw new Error(`Invalid seed length: ${seed.length}, expected 32`);
  }

  const versionByte = 18 << 3; // 144

  const payload = new Uint8Array(33);
  payload[0] = versionByte;
  payload.set(seed, 1);

  const checksum = crc16xmodem(payload);

  const full = new Uint8Array(35);
  full.set(payload, 0);
  full.set(checksum, 33);

  return base32Encode(full);
}

// ============================================================================
// Combined Operations (for iframe handler)
// ============================================================================

/**
 * Derive Stellar G-address from PRF output.
 * PRF → HKDF → ed25519 public key → StrKey G-address.
 */
export async function deriveRelayAddress(prfOutput: Uint8Array): Promise<string> {
  const { publicKey } = await deriveEd25519Keypair(prfOutput);
  return publicKeyToStellarAddress(publicKey);
}

/**
 * Sign a payload with PRF-derived ed25519 keypair.
 * Returns signature + public key, then discards the private key.
 *
 * @param prfOutput - 32-byte PRF output from WebAuthn
 * @param payload - Message to sign (typically a transaction hash)
 * @returns signature (64 bytes), publicKey (32 bytes), address (G-...)
 */
export async function signWithRelay(
  prfOutput: Uint8Array,
  payload: Uint8Array
): Promise<{
  signature: Uint8Array;
  publicKey: Uint8Array;
  address: string;
}> {
  const { publicKey, privateKey } = await deriveEd25519Keypair(prfOutput);
  const signature = await signEd25519(payload, privateKey);
  const address = publicKeyToStellarAddress(publicKey);

  // Private key goes out of scope here — no references remain.
  // The Uint8Array will be garbage collected.

  return { signature, publicKey, address };
}
